import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { FaUpload, FaTimes, FaFileAlt, FaCloudUploadAlt } from 'react-icons/fa';


interface DocumentUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (file: File) => Promise<any>;
  title?: string;
  maxFileSize?: number; // in MB
  acceptedTypes?: string[];
  description?: string;
  allowMultiple?: boolean;
  maxFiles?: number;
  /** Shows the generic "uploaded" message. Callers that show their own message turn it off. */
  showSuccessToast?: boolean;
}

export const DocumentUploadModal: React.FC<DocumentUploadModalProps> = ({
  isOpen,
  onClose,
  onUpload,
  title = "Upload Document",
  maxFileSize = 10,
  acceptedTypes = [],
  description = "Select a file or drag and drop it here",
  allowMultiple = false,
  maxFiles = 20,
  showSuccessToast = true
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadQueue, setUploadQueue] = useState<{file: File, status: 'pending' | 'uploading' | 'completed' | 'error', progress: number, error?: string}[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cancelRequestedRef = useRef(false);

  // Reset cancel flag when modal opens
  useEffect(() => {
    if (isOpen) {
      cancelRequestedRef.current = false;
    }
  }, [isOpen]);

  const validateFile = (file: File): string | null => {
    // Check file size
    const fileSizeMB = file.size / (1024 * 1024);
    console.log(`File size: ${fileSizeMB.toFixed(2)}MB, Max allowed: ${maxFileSize}MB`);
    
    if (fileSizeMB > maxFileSize) {
      const errorMsg = `File "${file.name}" (${fileSizeMB.toFixed(2)}MB) is too large. Maximum size is ${maxFileSize}MB. Please choose a smaller file.`;
      console.error(errorMsg);
      return errorMsg;
    }

    // Check file type if specified
    if (acceptedTypes.length > 0) {
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      const mimeType = file.type.toLowerCase();
      
      const isValidType = acceptedTypes.some(type => 
        type.startsWith('.') ? 
          fileExtension === type.slice(1) : 
          mimeType.includes(type)
      );
      
      if (!isValidType) {
        return `File type not supported. Accepted types: ${acceptedTypes.join(', ')}`;
      }
    }

    return null;
  };

  const handleFileUpload = async (file: File) => {
    if (cancelRequestedRef.current) return;
    console.log(`Attempting to upload file: ${file.name}, size: ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
    
    // Clear previous validation error
    setValidationError(null);
    
    const validationErrorMsg = validateFile(file);
    if (validationErrorMsg) {
      console.error('File validation failed:', validationErrorMsg);
      setValidationError(validationErrorMsg);
      toast.error(validationErrorMsg, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return;
    }

    try {
      setIsUploading(true);
      setUploadProgress(0);
      
      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (cancelRequestedRef.current) {
            clearInterval(progressInterval);
            return prev;
          }
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + Math.random() * 20;
        });
      }, 200);

      await onUpload(file);
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      if (cancelRequestedRef.current) {
        setIsUploading(false);
        return;
      }

      setTimeout(() => {
        if (!cancelRequestedRef.current) {
          if (showSuccessToast) toast.success('Document uploaded successfully!');
        }
        setValidationError(null);
        if (!cancelRequestedRef.current) {
          onClose();
        }
        setUploadProgress(0);
      }, 500);
      
    } catch (error) {
      console.error('Failed to upload document:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      
      // Handle specific error types
      if (errorMessage.includes('Failed to fetch') || errorMessage.includes('fetch')) {
        toast.error('File is too large. Please try a smaller file.');
      } else if (errorMessage.includes('405')) {
        toast.error('Upload not supported by API. Please contact support.');
      } else if (errorMessage.includes('401') || errorMessage.includes('403')) {
        toast.error('You do not have permission to upload documents.');
      } else if (errorMessage.includes('413') || errorMessage.includes('Content Too Large')) {
        const fileSizeMB = (file.size / 1024 / 1024).toFixed(2);
        toast.error(`File "${file.name}" (${fileSizeMB}MB) is too large. Maximum size is 10MB. Please try a smaller file.`);
      } else {
        toast.error(`Failed to upload document: ${errorMessage}`);
      }
      
      setUploadProgress(0);
    } finally {
      setIsUploading(false);
    }
  };

  const handleMultipleFiles = (files: File[]) => {
    const validFiles: File[] = [];
    const errors: string[] = [];

    // Validate each file
    files.forEach(file => {
      const error = validateFile(file);
      if (error) {
        errors.push(error);
      } else {
        validFiles.push(file);
      }
    });

    // Check total file count
    const currentCount = selectedFiles.length;
    if (currentCount + validFiles.length > maxFiles) {
      errors.push(`Cannot upload more than ${maxFiles} files. Current: ${currentCount}, trying to add: ${validFiles.length}`);
      return;
    }

    // Show errors if any
    if (errors.length > 0) {
      errors.forEach(error => toast.error(error));
    }

    // Add valid files to queue and start upload immediately
    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles]);
      const newQueueItems = validFiles.map(file => ({
        file,
        status: 'pending' as const,
        progress: 0
      }));
      setUploadQueue(prev => {
        const updatedQueue = [...prev, ...newQueueItems];
        // Start upload immediately after state update
        setTimeout(() => {
          uploadFilesFromQueue(updatedQueue);
        }, 100);
        return updatedQueue;
      });
      toast.success(`Starting upload of ${validFiles.length} file(s)...`);
    }
  };

  const uploadFilesFromQueue = async (queueToUpload: typeof uploadQueue) => {
    const pendingFiles = queueToUpload.filter(item => item.status === 'pending');
    if (pendingFiles.length === 0) return;

    setIsUploading(true);
    
    for (const item of pendingFiles) {
      if (cancelRequestedRef.current) break;
      // Update status to uploading
      setUploadQueue(prev => prev.map(q => 
        q.file === item.file ? { ...q, status: 'uploading', progress: 0 } : q
      ));

      try {
        // Simulate progress
        let progress = 0;
        const progressInterval = setInterval(() => {
          if (cancelRequestedRef.current) {
            clearInterval(progressInterval);
            return;
          }
          progress = Math.min(85, progress + Math.random() * 15);
          setUploadQueue(prev => prev.map(q => 
            q.file === item.file ? { ...q, progress } : q
          ));
        }, 200);

        await onUpload(item.file);
        
        clearInterval(progressInterval);
        if (cancelRequestedRef.current) {
          setIsUploading(false);
          return;
        }
        setUploadQueue(prev => prev.map(q => 
          q.file === item.file ? { ...q, status: 'completed', progress: 100 } : q
        ));
      } catch (error) {
        if (cancelRequestedRef.current) {
          setIsUploading(false);
          return;
        }
        setUploadQueue(prev => prev.map(q => 
          q.file === item.file ? { 
            ...q, 
            status: 'error', 
            progress: 0,
            error: error instanceof Error ? error.message : 'Upload failed'
          } : q
        ));
      }
    }

    setIsUploading(false);
    
    // Check final results
    if (cancelRequestedRef.current) return;
    setTimeout(() => {
      setUploadQueue(currentQueue => {
        const completed = currentQueue.filter(q => q.status === 'completed').length;
        const failed = currentQueue.filter(q => q.status === 'error').length;
        
        if (completed > 0) {
          if (!cancelRequestedRef.current) {
            toast.success(`Successfully uploaded ${completed} file(s)`);
          }
          
          // Auto-close modal after successful upload
          if (!cancelRequestedRef.current) {
            setTimeout(() => {
              if (!cancelRequestedRef.current) {
                handleClose();
              }
            }, 1500);
          }
        }
        if (failed > 0) {
          if (!cancelRequestedRef.current) {
            toast.error(`Failed to upload ${failed} file(s)`);
          }
        }
        
        return currentQueue;
      });
    }, 100);
  };

  // Keep for potential future use or manual upload trigger
  // const uploadAllFiles = async () => {
  //   uploadFilesFromQueue(uploadQueue);
  // };

  const removeFile = (fileToRemove: File) => {
    setSelectedFiles(prev => prev.filter(f => f !== fileToRemove));
    setUploadQueue(prev => prev.filter(q => q.file !== fileToRemove));
  };

  const clearAll = () => {
    setSelectedFiles([]);
    setUploadQueue([]);
  };

  // Clear state when modal closes
  const handleClose = () => {
    cancelRequestedRef.current = true;
    setSelectedFiles([]);
    setUploadQueue([]);
    setValidationError(null);
    setIsUploading(false);
    setUploadProgress(0);
    onClose();
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      if (allowMultiple) {
        handleMultipleFiles(files);
      } else {
        // For single file, upload immediately
        handleFileUpload(files[0]);
      }
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      if (allowMultiple) {
        handleMultipleFiles(files);
      } else {
        // For single file, upload immediately
        handleFileUpload(files[0]);
      }
    }
  }, [allowMultiple]);



  if (!isOpen) return null;

  return createPortal(
    <div 
      className="fixed inset-0 bg-white bg-opacity-80 flex items-center justify-center"
      style={{ zIndex: 999999 }}
    >
      <button
        onClick={handleClose}
        className="fixed top-4 right-4 text-[#aaa] hover:text-[#555] p-2"
        style={{ zIndex: 1000000 }}
      >
        <FaTimes className="w-6 h-6" />
      </button>
      <div className="bg-white rounded-xl border border-[#eaeaea] max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[#eaeaea]">
          <h3 className="text-lg font-semibold text-[#171717]">{title}</h3>
          <button
            onClick={handleClose}
            className="text-[#aaa] hover:text-[#555] transition-colors"
          >
            <FaTimes className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Upload Area */}
          <div
            className={`
              relative border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200
              ${isDragOver 
                ? 'border-blue-400 bg-[#f5f5f5]' 
                : 'border-[#eaeaea] hover:border-[#bbb]'
              }
              ${isUploading ? 'pointer-events-none opacity-75' : 'cursor-pointer'}
            `}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => !isUploading && fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              className="hidden"
              accept={acceptedTypes.length > 0 ? acceptedTypes.join(',') : '*/*'}
              disabled={isUploading}
              multiple={allowMultiple}
            />

            {isUploading ? (
              <div className="space-y-4">
                <div className="flex justify-center">
                  <FaCloudUploadAlt className="w-12 h-12 text-[#888] animate-pulse" />
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium text-[#444]">Uploading...</p>
                  <div className="w-full bg-[#eaeaea] rounded-full h-2">
                    <div 
                      className="bg-[#171717] h-2 rounded-full transition-all duration-300 ease-out"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <p className="text-xs text-[#888]">{Math.round(uploadProgress)}%</p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex justify-center">
                  <FaUpload className={`w-12 h-12 ${isDragOver ? 'text-[#888]' : 'text-[#aaa]'} transition-colors`} />
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium text-[#444]">
                    {isDragOver ? 
                      (allowMultiple ? 'Drop files here' : 'Drop file here') : 
                      (allowMultiple ? 
                        `Drop files or click to browse (up to ${maxFiles})` : 
                        description
                      )
                    }
                  </p>
                  <p className="text-xs text-[#888]">
                    Max size: {maxFileSize}MB per file
                  </p>
                  
                  {/* Validation Error */}
                  {validationError && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-xs font-bold">!</span>
                        </div>
                        <p className="text-sm text-red-700 font-medium">{validationError}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* File Queue (for multiple files) */}
          {allowMultiple && uploadQueue.length > 0 && (
            <div className="mt-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-[#171717]">
                  Upload Queue ({uploadQueue.length}/{maxFiles})
                </h3>
                {!isUploading && (
                  <button
                    onClick={clearAll}
                    className="text-xs text-[#888] hover:text-[#dc2626]"
                  >
                    Clear All
                  </button>
                )}
              </div>
              
              <div className="space-y-2">
                {uploadQueue.map((item, index) => (
                  <div key={index} className="flex items-center space-x-3 p-3 bg-white border border-[#eaeaea] rounded-lg">
                    {/* Status Icon */}
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${
                      item.status === 'pending' ? 'bg-[#f5f5f5] text-[#888]' :
                      item.status === 'uploading' ? 'bg-[#f5f5f5] text-[#171717]' :
                      item.status === 'completed' ? 'bg-green-100 text-green-600' :
                      'bg-red-100 text-red-600'
                    }`}>
                      {item.status === 'pending' && '📄'}
                      {item.status === 'uploading' && '⬆️'}
                      {item.status === 'completed' && '✅'}
                      {item.status === 'error' && '❌'}
                    </div>
                    
                    {/* File Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-[#171717] truncate pr-2">
                          {item.file.name}
                        </p>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-[#888]">
                            {(item.file.size / (1024 * 1024)).toFixed(1)} MB
                          </span>
                          {item.status === 'pending' && !isUploading && (
                            <button
                              onClick={() => removeFile(item.file)}
                              className="text-[#aaa] hover:text-red-500 transition-colors"
                            >
                              <FaTimes className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      </div>
                      
                      {/* Progress Bar */}
                      {item.status === 'uploading' && (
                        <div className="w-full bg-[#eaeaea] rounded-full h-2 mt-2">
                          <div 
                            className="bg-[#171717] h-2 rounded-full transition-all duration-300"
                            style={{ width: `${item.progress}%` }}
                          />
                        </div>
                      )}
                      
                      {/* Error Message */}
                      {item.error && (
                        <p className="text-xs text-red-500 mt-1 font-medium">{item.error}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-[#444] bg-[#f5f5f5] border border-[#eaeaea] rounded-lg hover:bg-[#eaeaea] focus:outline-none focus:ring-0 transition-colors"
            >
              Cancel
            </button>
            {allowMultiple ? (
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="px-4 py-2 text-sm font-medium text-white bg-[#171717] border border-transparent rounded-lg hover:bg-[#333] focus:outline-none focus:ring-0 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <FaFileAlt className="w-4 h-4 inline mr-2" />
                {isUploading ? 'Uploading...' : 'Add More Files'}
              </button>
            ) : (
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="px-4 py-2 text-sm font-medium text-white bg-[#171717] border border-transparent rounded-lg hover:bg-[#333] focus:outline-none focus:ring-0 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <FaFileAlt className="w-4 h-4 inline mr-2" />
                Choose File
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
